/**
 * Shell for the isolated design-system routes: nav, footer, head tags.
 * These routes are unlinked and noindex until the system is adopted.
 */
import { useEffect, type ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, NavLink } from 'react-router-dom'
import { ButtonLink, DsRoot, ThemeSwitch } from './primitives'

/*
  DIRECTION CONTRACT (seed 4288a117, revised by the owner 2026-08-22)
  THESIS: Show what AI agents understand about your business, in your words,
    and show it is confirmed. Refuses anything that looks like code: no trees,
    braces, line numbers, or mono body text; no eyebrows, no purple.
  OWN-WORLD: paper white and a cool quiet band, near-black ink, one electric
    accent blue (Bolt) for links and the linked highlight; "Confirmed" is the
    deep tone of that same blue and "Needs a look" is a saturated amber fill
    with near-black text, the only other colours; Schibsted Grotesk
    everywhere, Fragment Mono for timestamps only; 12/16/20px radii, layered
    soft shadows, 17px body, generous air.
  IMAGERY: show the real thing. Five registers only: the running mockup built
    from live parts, the capture of a real screen at true scale, the crop of
    it, the portrait of a real customer, and a measured grid in the margins.
    No illustration, no abstract art, no stock stand-ins, no invented quotes.
  MOTION: the page never moves, the product does. No scroll reveals, no
    parallax, no drifting logo strips, no counters. A running mockup plays
    only on screen, hands over the moment a person points at it, and shows
    the resolved state under reduced motion.
  STORY: "AI agents get my business right, and I can see that it is still
    right." Then: check my site.
  FIRST VIEWPORT: nav; headline and one sentence; the address bar; then one
    raised card: your page on the left, "What AI agents understand about you"
    as a plain-language list on the right with a proof card at its top.
  FORM: the comparison card, light, prose-led, Apple-calm.
  FINISH: unreviewed and undocumented is unfinished; this build ends with the
    finish review, the verdict, DESIGN.md, and every shipping raster carrying
    its provenance.
*/

/* Emitted into the DOM so the contract survives the production build. */
const CONTRACT_COMMENT =
  '<!-- impeccable direction contract, seed 4288a117 (revised). THESIS: show what AI agents understand about your business, in your words, and show it is confirmed; nothing that looks like code. OWN-WORLD: paper white, near-black ink, one electric accent blue (Bolt), Confirmed in the deep tone of the same blue, Needs a look as a saturated amber fill with near-black text; Schibsted Grotesk everywhere, Fragment Mono for timestamps only; 12/16/20px radii, soft layered shadows. IMAGERY: show the real thing, in five registers only: running mockup, capture, crop, portrait of a real customer, measured grid. No illustration, no abstract art, no stock stand-ins, no invented quotes. MOTION: the page never moves, the product does. FINISH: unreviewed and undocumented is unfinished; this build ends with the finish review, the verdict, DESIGN.md, and every shipping raster carrying its provenance. -->'

const MARKETING_LINKS = [
  { to: '/', label: 'Product' },
  { to: '/team-workflow', label: 'Agencies' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/customers', label: 'Customers' },
  { to: '/docs', label: 'Help' },
]

const FOOTER: { title: string; items: { to: string; label: string }[] }[] = [
  {
    title: 'Product',
    items: [
      { to: '/', label: 'AI Readiness Scan' },
      { to: '/schema-markup', label: 'How it works' },
      { to: '/team-workflow', label: 'Agencies' },
      { to: '/pricing', label: 'Pricing' },
    ],
  },
  {
    title: 'Company',
    items: [
      { to: '/customers', label: 'Customers' },
      { to: '/methodology', label: 'Methodology' },
      { to: '/security', label: 'Security' },
      { to: '/contact', label: 'Contact' },
    ],
  },
  {
    title: 'Legal',
    items: [
      { to: '/terms', label: 'Terms' },
      { to: '/privacy', label: 'Privacy' },
      { to: '/cookies', label: 'Cookies' },
    ],
  },
]

const LINKS = [
  { to: '/design-system', label: 'Design system', end: true },
  { to: '/ui-kit', label: 'UI kit' },
  { to: '/design-system/specimen', label: 'Specimen' },
]

/**
 * A link to a section has to land on that section.
 *
 * The browser tries to honour the hash before React has rendered anything,
 * finds nothing to scroll to, and gives up at the top of the page. Every
 * #section link anybody shares then arrives in the wrong place, which is
 * exactly what happened the first time these pages were shared. This does the
 * jump once the sections exist, and again whenever the hash changes.
 */
/**
 * The brand mark, in one place so it can be replaced in one place.
 *
 * The mark is a supplied brand object, not interface chrome. It carries its
 * own blue and its own shading, and the ban on gradients elsewhere in the
 * system does not reach it: the rule is that the interface never draws one,
 * not that the logo cannot have one.
 */
/*
  The brand mark, and there is one.

  This pointed at `super-schema-icon-mark-v2.png`, a rounded ribbon S at
  400x509, while `BrandMark` in primitives.tsx pointed at
  `super-schema-mark.png`, the angular bolt at 512x512. Two different logos,
  and the split ran along the wrong line: the bolt was on every surface a
  customer sees, the global header, both footers, the onboarding shells and
  the lifecycle emails, and the ribbon was on the loading screen, the
  not-found page and the design-system routes. So the first thing a person
  saw while the app booted was not the logo they saw a second later.

  Both now resolve to the same file. `BrandMark` keeps its own element
  because it is square-sized by a single `size`; this one keeps height-led
  sizing. They draw the same picture.
*/
export const BRAND_MARK = '/design-system/brand/super-schema-mark.png'

export function Mark({ size = 26, className }: { size?: number; className?: string }) {
  return (
    <img
      className={className}
      src={BRAND_MARK}
      alt=""
      /* Its real proportions, so nothing squashes it and the space it will
         take is known before it loads. Square since the mark became the
         bolt: 400x509 here would have reserved the wrong box and shifted
         the layout as it loaded. */
      width={512}
      height={512}
      /*
        `flex: none` because `max-width: 100%` collapses a height-led image
        to nothing inside a shrink-to-fit flex parent: the parent's width
        depends on the child, the child's max-width depends on the parent,
        and the browser resolves the circle at zero. Ten of the fourteen
        marks on /ui-kit were rendering 0px wide, with the old asset too.
      */
      style={{ height: size, width: 'auto', maxWidth: 'none', flex: 'none' }}
      decoding="async"
    />
  )
}

export function useHashJump() {
  useEffect(() => {
    const jump = () => {
      const id = window.location.hash.slice(1)
      if (!id) return
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ block: 'start' })
    }
    jump()
    window.addEventListener('hashchange', jump)
    return () => window.removeEventListener('hashchange', jump)
  }, [])
}

export function DsPage({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <DsRoot>
      <Helmet>
        <title>{title} | SuperSchema</title>
        <meta name="description" content={description} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div hidden dangerouslySetInnerHTML={{ __html: CONTRACT_COMMENT }} />
      {children}
    </DsRoot>
  )
}

export function DsNav({ marketing }: { marketing?: boolean }) {
  return (
    <header className="ds-nav">
      <div className="ds-container ds-nav__inner">
        <NavLink to="/design-system" className="ds-nav__brand" aria-label="SuperSchema">
          <Mark size={26} />
          <span>SuperSchema</span>
        </NavLink>
        {marketing ? (
          <nav className="ds-nav__links" aria-label="Primary">
            {MARKETING_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className="ds-nav__link">
                {l.label}
              </NavLink>
            ))}
          </nav>
        ) : (
          <nav className="ds-nav__links" aria-label="Design system">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className="ds-nav__link">
                {l.label}
              </NavLink>
            ))}
          </nav>
        )}
        <div className="ds-nav__actions">
          <ThemeSwitch />
          {marketing ? (
            <>
              <ButtonLink to="/sign-in" variant="ghost" size="sm" className="ds-nav__login">
                Log in
              </ButtonLink>
              <ButtonLink to="#scan" size="sm">
                Scan my site
              </ButtonLink>
            </>
          ) : (
            <ButtonLink to="/design-system/specimen" variant="secondary" size="sm">
              View specimen
            </ButtonLink>
          )}
        </div>
      </div>
    </header>
  )
}

export function DsFooter() {
  return (
    <footer className="ds-footer">
      <div className="ds-container ds-footer__grid">
        <div>
          <div className="ds-nav__brand" style={{ marginBottom: 8 }}>
            <Mark size={22} />
            <span>SuperSchema</span>
          </div>
          <p style={{ margin: 0, maxWidth: '36ch' }}>Make sure AI agents get your business right, and know it stayed right.</p>
        </div>
        {FOOTER.map((col) => (
          <div key={col.title}>
            <h4>{col.title}</h4>
            <ul>
              {col.items.map((i) => (
                <li key={i.to + i.label}>
                  <Link to={i.to}>{i.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </footer>
  )
}
